(function () {
  if (window.GBOfferSlider?.booted) {
    window.GBOfferSlider.init(document);
    return;
  }

  const SELECTOR = '[data-gb-offer-slider]';
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();

  function asBoolean(value) {
    return String(value) === 'true';
  }

  class OfferSlider {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-track]');
      this.prev = root.querySelector('[data-prev]');
      this.next = root.querySelector('[data-next]');
      this.dotsWrap = root.querySelector('[data-dots]');
      this.slides = [];
      this.dots = [];
      this.rafId = 0;
      this.autoplayTimer = 0;
      this.userInteracted = false;
      this.autoplayEnabled = asBoolean(root.dataset.autoplay);
      this.autoplaySpeedMs = (parseInt(root.dataset.autoplaySpeed || '4', 10) || 4) * 1000;
    }

    init() {
      if (!this.track) return;
      this.slides = Array.from(this.track.querySelectorAll('[data-slide]'));
      if (this.slides.length < 2) return;

      if (this.prev && this.next) {
        this.root.classList.add('has-arrows');
      }

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onPrev = this.onPrev.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onDotsClick = this.onDotsClick.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });

      this.prev?.addEventListener('click', this.onPrev);
      this.next?.addEventListener('click', this.onNext);
      this.dotsWrap?.addEventListener('click', this.onDotsClick);

      this.root.addEventListener('mouseenter', this.onMouseEnter);
      this.root.addEventListener('mouseleave', this.onMouseLeave);

      this.renderDots();
      this.refresh();
      this.startAutoplay();
    }

    destroy() {
      window.cancelAnimationFrame(this.rafId);
      window.clearTimeout(this.autoplayTimer);

      this.track?.removeEventListener('scroll', this.onScroll);
      this.track?.removeEventListener('pointerdown', this.onPointerDown);
      window.removeEventListener('resize', this.onResize);
      this.prev?.removeEventListener('click', this.onPrev);
      this.next?.removeEventListener('click', this.onNext);
      this.dotsWrap?.removeEventListener('click', this.onDotsClick);
      this.root.removeEventListener('mouseenter', this.onMouseEnter);
      this.root.removeEventListener('mouseleave', this.onMouseLeave);
    }

    onScroll() {
      if (this.rafId) return;
      this.rafId = window.requestAnimationFrame(() => {
        this.rafId = 0;
        this.refresh();
      });
    }

    onResize() {
      this.refresh();
    }

    onPointerDown() {
      this.markInteracted();
    }

    onPrev() {
      this.markInteracted();
      this.scrollBySlide(-1);
    }

    onNext() {
      this.markInteracted();
      this.scrollBySlide(1);
    }

    onDotsClick(event) {
      const target = event.target.closest('.gb-offer-slider__dot');
      if (!target) return;
      const index = parseInt(target.dataset.index || '', 10);
      if (Number.isNaN(index)) return;
      this.markInteracted();
      this.scrollToIndex(index);
    }

    onMouseEnter() {
      window.clearTimeout(this.autoplayTimer);
    }

    onMouseLeave() {
      this.startAutoplay();
    }

    markInteracted() {
      this.userInteracted = true;
      window.clearTimeout(this.autoplayTimer);
    }

    getStep() {
      if (this.slides.length < 2) {
        return this.track.clientWidth;
      }
      return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
    }

    scrollBySlide(direction) {
      this.track.scrollBy({
        left: this.getStep() * direction,
        behavior: prefersReducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    scrollToIndex(index) {
      const slide = this.slides[index];
      if (!slide) return;
      this.track.scrollTo({
        left: slide.offsetLeft,
        behavior: prefersReducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    getActiveIndex() {
      const center = this.track.scrollLeft + this.track.clientWidth / 2;
      let bestIndex = 0;
      let bestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
        const distance = Math.abs(slideCenter - center);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
        }
      });

      return bestIndex;
    }

    renderDots() {
      if (!this.dotsWrap) return;
      this.dotsWrap.innerHTML = '';
      this.dots = [];

      this.slides.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'gb-offer-slider__dot';
        dot.dataset.index = String(index);
        dot.setAttribute('aria-label', `Go to offer ${index + 1}`);
        this.dotsWrap.appendChild(dot);
        this.dots.push(dot);
      });
    }

    refresh() {
      const max = this.track.scrollWidth - this.track.clientWidth - 1;

      if (this.prev && this.next) {
        this.prev.disabled = this.track.scrollLeft <= 1;
        this.next.disabled = this.track.scrollLeft >= max;
      }

      if (this.dots.length) {
        const active = this.getActiveIndex();
        this.dots.forEach((dot, index) => {
          dot.classList.toggle('is-active', index === active);
        });
      }
    }

    startAutoplay() {
      if (!this.autoplayEnabled || prefersReducedMotion.matches || this.userInteracted) return;

      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = window.setTimeout(() => {
        const max = this.track.scrollWidth - this.track.clientWidth - 1;
        if (this.track.scrollLeft >= max) {
          this.track.scrollTo({ left: 0, behavior: prefersReducedMotion.matches ? 'auto' : 'smooth' });
        } else {
          this.scrollBySlide(1);
        }
        this.startAutoplay();
      }, this.autoplaySpeedMs);
    }
  }

  function init(scope) {
    const container = scope || document;
    container.querySelectorAll(SELECTOR).forEach((element) => {
      if (instances.has(element)) return;
      const slider = new OfferSlider(element);
      slider.init();
      instances.set(element, slider);
    });
  }

  function destroy(scope) {
    const container = scope || document;
    container.querySelectorAll(SELECTOR).forEach((element) => {
      const instance = instances.get(element);
      if (!instance) return;
      instance.destroy();
      instances.delete(element);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));

  window.GBOfferSlider = { booted: true, init, destroy };
})();
